import React, { MouseEventHandler } from 'react';
import { Container, Typography, Box, Tooltip } from '@mui/material';
import { styled, useTheme } from '@mui/material/styles';
import { GameSessionState } from '@righton/networking';
import CorrectStars from '../img/CorrectStars.svg';
import CorrectStars_Mirrored from '../img/CorrectStars_Mirrored.svg';
import SelectedAnswer from '../img/SelectedAnswer.svg';
import PlayerCorrectImage from '../img/PlayerCorrectImage.svg';
import CorrectAnswerImage from '../img/correctAnswerImage.svg';
import { AnswerState, monsterMap } from '../lib/PlayModels';
import NewPointsIndicator from './NewPointsIndicator';

const ResultSelectorDefault = styled(Container)(({ theme }) => ({
  width: '100%',
  minHeight: '42px',
  borderRadius: '22px',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  textTransform: 'none',
  backgroundColor: theme.palette.primary.lightGrey,
  maxWidth: '100%', // overwrite MUI default maxWidth
  paddingLeft: `${theme.sizing.smPadding}px`, // overwrite MUI default padding
  paddingRight: `${theme.sizing.smPadding}px`,
}));

const ResultSelectorCorrect = styled(ResultSelectorDefault)(({ theme }) => ({
  backgroundColor: theme.palette.primary.correctColor,
}));

const CorrectStarsStyled = styled('img')({
  position: 'absolute',
  width: '16px',
  height: '16px',
});

interface ResultSelectorProps {
  answerStatus: AnswerState;
  letterCode: string;
  answerText: string;
  teamAvatar: number;
  percentageText?: string;
  currentState?: GameSessionState;
  isShortAnswerEnabled?: boolean;
  correctCard?: boolean;
  newPoints?: number;
}

export default function ResultSelector({
  answerStatus,
  letterCode,
  answerText,
  teamAvatar,
  percentageText,
  currentState,
  isShortAnswerEnabled,
  correctCard,
  newPoints
}: ResultSelectorProps) {
  const theme = useTheme();
  
  const imageMap = {
    [AnswerState.DEFAULT]: '',
    [AnswerState.CORRECT]: CorrectAnswerImage,
    [AnswerState.PLAYER_SELECTED_CORRECT]: PlayerCorrectImage,
    [AnswerState.SELECTED]: monsterMap[teamAvatar].answerSelect,
    [AnswerState.PREVIOUS]: '',
    [AnswerState.OTHER]: '',
  };

  // disables context menu when longclicking on image
  const handleContextMenu: MouseEventHandler<HTMLElement> = (event) => {
    event.preventDefault();
  };
  const correctAnswerIcon = newPoints && newPoints > 0
    ? <NewPointsIndicator newPoints={newPoints} score={0} currentState={currentState ?? GameSessionState.PHASE_1_DISCUSS} />
    : <img src={PlayerCorrectImage} style={{ position: 'relative', width: `${theme.sizing.mdPadding}px`, height: `${theme.sizing.mdPadding}px`, paddingTop: '2px', WebkitTouchCallout: 'none' }} alt="SelectedAnswerImage" onContextMenu={handleContextMenu} />;

  const image = (
    <img
      src={imageMap[answerStatus]}
      style={{
        position: 'relative',
        width: `${theme.sizing.mdPadding}px`,
        height: `${theme.sizing.mdPadding}px`,
        paddingTop: '2px',
        // disable touch callout when longclicking on image
        WebkitTouchCallout: 'none',
      }}
      alt="SelectedAnswerImage"
      // disable context menu when longclicking on image
      onContextMenu={handleContextMenu}
    />
  );
  const resultContents = (
    <>
      <Box sx={{ display: 'flex', alignItems: 'center', flex: 1, minWidth: 0 }}>
        {!(isShortAnswerEnabled && currentState === GameSessionState.PHASE_1_DISCUSS) && !(isShortAnswerEnabled && correctCard) && (
          <Typography
            variant="h2"
            sx={{
              paddingLeft: '1px',
              lineHeight: 1,
              color: correctCard ? '#384466' : `${theme.palette.designSystem.surface.play}`,
            }}
          >
            {letterCode.toUpperCase()}
          </Typography>
        )}
        <Typography
          variant="textLabel"
          sx={{
            paddingLeft: `${theme.sizing.smPadding}px`,
            paddingRight: `${theme.sizing.lgPadding}px`,
            whiteSpace: 'pre-line',
            fontWeight: 400,
            color:  `${theme.palette.designSystem.surface.play}`
          }}
        >
          {answerText}
        </Typography>

      </Box>
      <Box style={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
        {currentState === GameSessionState.PHASE_2_DISCUSS && ( 
          <Typography
            variant="body2"
            sx={{
              paddingRight:
                answerStatus === AnswerState.CORRECT ||
                answerStatus === AnswerState.PREVIOUS ||
                answerStatus === AnswerState.SELECTED
                  ? `${theme.sizing.xSmPadding}px`
                  : `${theme.sizing.mdPadding}px`,
            }}
          >
            {percentageText}
          </Typography>
        )}
        {answerStatus === AnswerState.PLAYER_SELECTED_CORRECT ? correctAnswerIcon : (
          answerStatus !== AnswerState.PREVIOUS &&
          answerStatus !== AnswerState.DEFAULT &&
          (answerStatus === AnswerState.SELECTED ? (
            <Tooltip
              title="Your Phase 2 Answer"
              placement="top"
              arrow
              enterTouchDelay={0}
              leaveTouchDelay={300}
            >
              {image}
            </Tooltip>
          ) : (
            image
          ))
        )}
      </Box>
    </>
  );
  switch (answerStatus) {
    case AnswerState.CORRECT:
      return (
        <Box sx={{ width: '100%' }}>
          <ResultSelectorCorrect>{resultContents}</ResultSelectorCorrect>
        </Box>
      );
    case AnswerState.PLAYER_SELECTED_CORRECT:
      return (
        <Box sx={{width: '100%', display: 'flex', alignItems: 'center'}}>
          <Box sx={{ width: '100%' }}>
            {currentState === GameSessionState.PHASE_1_DISCUSS && (
              <Box sx={{ position: 'relative', height: 0, width: '100%' }}>
                <CorrectStarsStyled
                  src={CorrectStars}
                  alt="Stars icon that denotes player is correct"
                  style={{ top: -5, left: 0 }}
                />
                <CorrectStarsStyled
                  src={CorrectStars}
                  alt="Stars icon that denotes player is correct"
                  style={{ top: -5, right: 10 }}
                />
                <CorrectStarsStyled
                  src={CorrectStars_Mirrored}
                  alt="Stars icon that denotes player is correct"
                  style={{ top: 30, right: 0 }}
                />
              </Box>
            )}
            <ResultSelectorCorrect>{resultContents}</ResultSelectorCorrect>
          </Box>
        </Box>
      );
    case AnswerState.SELECTED:
    case AnswerState.DEFAULT:
    default:
      return <ResultSelectorDefault>{resultContents}</ResultSelectorDefault>;
  }
}
